import { useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { loginApi } from '@/app/login/api';
import { useSession } from '@/app/login/session';
import { NavAccess, NavRoute } from '@/app/navigation';
import { EmptyState } from '@/shared/ui/atoms';

/**
 * 앱 셸 — 사이드바(섹션 → 화면) + 상단 바 + 본문.
 * 섹션이 늘어나도 이 파일은 그대로다. 목록은 navigation.ts에서 온다.
 *
 * 로그인을 강제하면(서버 `CONSOLE_LOGIN_REQUIRED`) 로그인하지 않은 사람은 로그인 화면으로 보내고, 사이드바에는
 * 계정이 가진 메뉴만 보인다. 주소를 직접 쳐서 권한 없는 화면에 들어오면 본문 대신 안내를 띄운다(API도 403이다).
 */
export function AppShell(): React.JSX.Element {
  const { pathname, search } = useLocation();
  const { session, error } = useSession();
  const current = NavRoute.find(pathname);
  // 접힌 섹션만 기억한다 — 기본은 "펴짐"이라 새 섹션이 숨지 않는다.
  const [collapsed, setCollapsed] = useState<string[]>([]);

  const toggle = (key: string) =>
    setCollapsed((keys) => (keys.includes(key) ? keys.filter((item) => item !== key) : [...keys, key]));

  if (error) return <div className="p-10"><EmptyState message={`세션을 확인하지 못했습니다 — ${error}`} /></div>;
  if (!session) return <div className="p-10 text-center text-muted">불러오는 중…</div>;
  if (session.required && !session.account) {
    return <Navigate to={`/login?next=${encodeURIComponent(`${pathname}${search}`)}`} replace />;
  }

  const menus = session.required ? (session.account?.menus ?? []) : null;
  const sections = NavAccess.sections(menus);
  const allowed = !current || NavAccess.allows(menus, current.section, current.item);

  return (
    <div className="grid min-h-screen grid-cols-[232px_1fr]">
      <aside className="sticky top-0 flex h-screen flex-col gap-4 border-r border-line bg-surface px-3 py-4">
        <div className="flex shrink-0 items-center gap-2.5 px-1.5 py-1">
          <span className="grid size-8 place-items-center rounded-md bg-accent text-xs font-bold text-white">
            ND
          </span>
          <span>
            <strong className="block text-[13px]">ND Console</strong>
            <span className="block text-[11px] text-muted">개발팀 내부 어드민</span>
          </span>
        </div>

        {/*
         * 메뉴는 계속 늘어난다 — 로고와 하단 안내는 제자리에 두고 목록만 스크롤시켜,
         * 화면 높이보다 항목이 많아도 맨 아래 메뉴까지 닿는다.
         */}
        <nav className="-mr-1 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain pr-1">
          {sections.length === 0 && <span className="px-2 text-[12px] text-faint">열 수 있는 메뉴가 없습니다. 관리자에게 권한을 요청해 주세요.</span>}
          {sections.map((section) => {
            // 접기는 사용자 의도다 — 현재 화면이 그 섹션 안이어도 접힌 상태를 유지한다.
            const open = !collapsed.includes(section.key);
            return (
              <div key={section.key}>
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => toggle(section.key)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13.5px] font-bold text-ink hover:bg-line-soft"
                >
                  <span className="text-[14px] text-muted">{section.glyph}</span>
                  <span className="flex-1">{section.label}</span>
                  {!open && current?.section.key === section.key && (
                    <span className="text-[11px] font-normal text-faint">{current.item.label}</span>
                  )}
                  <span className={`transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden>
                    ›
                  </span>
                </button>

                {open && (
                  <div className="mt-0.5 flex flex-col">
                    {section.items.filter((item) => !item.hidden).map((item) => (
                      <NavLink
                        key={NavRoute.pathOf(section, item)}
                        to={NavRoute.pathOf(section, item)}
                        end
                        className={({ isActive }) =>
                          `rounded-md py-1.5 pr-2.5 pl-7 text-[13px] ${
                            isActive || current?.item.activePath === item.path
                              ? 'bg-accent-soft font-semibold text-accent'
                              : 'text-muted hover:bg-line-soft hover:text-ink'
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="shrink-0 p-2 text-[11px] text-faint">메뉴는 navigation.ts에서 늘린다</div>
      </aside>

      <main className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-20 flex h-topbar items-center gap-3 border-b border-line bg-surface px-5">
          <div className="flex items-center gap-2">
            <span className="text-muted">{current?.section.label ?? '어드민'}</span>
            <span className="text-faint">/</span>
            <strong>{current?.item.label ?? ''}</strong>
          </div>
          {session.account && <AccountMenu name={session.account.name} email={session.account.email} />}
        </header>
        <div className="min-w-0 px-5 pt-4.5 pb-10">
          {allowed ? <Outlet /> : <EmptyState message="이 화면을 열 권한이 없습니다. 관리자에게 권한을 요청해 주세요." />}
        </div>
      </main>
    </div>
  );
}

function AccountMenu({ name, email }: { name: string; email: string }): React.JSX.Element {
  const logout = (): void => {
    // 로그아웃 요청이 실패해도(이미 만료 등) 로그인 화면으로 보낸다 — 남은 쿠키는 서버가 다음 요청에서 거절한다.
    void loginApi.logout().finally(() => window.location.assign('/login'));
  };
  return (
    <div className="ml-auto flex items-center gap-3 text-[12.5px]">
      <span className="text-muted" title={email}>
        {name}
      </span>
      <button type="button" onClick={logout} className="cursor-pointer text-[12px] text-accent hover:underline">
        로그아웃
      </button>
    </div>
  );
}
